import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../../components/layout/AppLayout'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { apiFetch } from '../../lib/api-client'
import { t } from '../../lib/i18n'

interface Property {
  id: string
  address: string
}

interface BBRLookup {
  found: boolean
  area_m2: number | null
  room_count: number | null
  floor: string | null
  usage_code: string | null
  usage_type: string | null
  usage_label: string | null
}

const USAGE_TYPES = ['bolig', 'erhverv', 'kontor', 'lager', 'garage', 'kælder', 'andet'] as const
type UsageType = (typeof USAGE_TYPES)[number]

// The legacy `type` (building category) is derived from the chosen anvendelse
// so the backend keeps a sensible value; `usage_type` is the visible driver.
const TYPE_FOR_USAGE: Record<UsageType, string> = {
  bolig: 'residential',
  erhverv: 'commercial',
  kontor: 'commercial',
  lager: 'storage',
  garage: 'garage',
  kælder: 'storage',
  andet: 'commercial',
}

type BBRStatus = 'idle' | 'loading' | 'found' | 'notfound'

export function LeaseCreatePage() {
  const strings = t()
  const navigate = useNavigate()

  const [properties, setProperties] = useState<Property[]>([])
  const [form, setForm] = useState<{
    property_id: string
    unit_no: string
    usage_type: UsageType
    area_m2: string
    room_count: string
    floor: string
  }>({ property_id: '', unit_no: '', usage_type: 'bolig', area_m2: '', room_count: '', floor: '' })
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const [bbrStatus, setBbrStatus] = useState<BBRStatus>('idle')
  // True while usage_type still reflects the BBR suggestion (cleared once the
  // user picks one manually) — drives the "foreslået fra BBR" hint.
  const [usageFromBbr, setUsageFromBbr] = useState(false)

  // Fetch BBR autofyld for the selected property and pre-fill the editable
  // fields. Best-effort: a miss just leaves the fields for manual entry.
  const lookupBbr = async (propertyId: string) => {
    if (!propertyId) {
      setBbrStatus('idle')
      return
    }
    setBbrStatus('loading')
    try {
      const data = await apiFetch<BBRLookup>(
        `/api/v1/leases/bbr-lookup?property_id=${encodeURIComponent(propertyId)}`,
      )
      if (!data.found) {
        setBbrStatus('notfound')
        return
      }
      setBbrStatus('found')
      // Only accept a suggested usage_type that's actually one of our buttons —
      // an unknown value would submit but show no active selection (broken state).
      const suggested = USAGE_TYPES.includes(data.usage_type as UsageType)
        ? (data.usage_type as UsageType)
        : null
      setForm((f) => ({
        ...f,
        area_m2: data.area_m2 != null ? String(data.area_m2) : f.area_m2,
        room_count: data.room_count != null ? String(data.room_count) : f.room_count,
        floor: data.floor != null ? data.floor : f.floor,
        usage_type: suggested ?? f.usage_type,
      }))
      setUsageFromBbr(suggested != null)
    } catch {
      setBbrStatus('notfound')
    }
  }

  useEffect(() => {
    apiFetch<{ items: Property[] }>('/api/v1/properties')
      .then((data) => {
        setProperties(data.items)
        if (data.items.length === 1) {
          const only = data.items[0].id
          setForm((f) => ({ ...f, property_id: only }))
          void lookupBbr(only)
        }
      })
      .catch(() => {})
    // lookupBbr is stable for this mount; intentional one-shot on load.

  }, [])

  const onPropertyChange = (propertyId: string) => {
    setForm((f) => ({ ...f, property_id: propertyId }))
    void lookupBbr(propertyId)
  }

  const usageLabel = (usage: UsageType) => {
    const map: Record<UsageType, string> = {
      bolig: strings.leases.usageBolig,
      erhverv: strings.leases.usageErhverv,
      kontor: strings.leases.usageKontor,
      lager: strings.leases.usageLager,
      garage: strings.leases.usageGarage,
      kælder: strings.leases.usageKaelder,
      andet: strings.leases.usageAndet,
    }
    return map[usage]
  }

  const validate = (): boolean => {
    const next: Partial<Record<keyof typeof form, string>> = {}
    if (!form.property_id) next.property_id = strings.auth.required
    if (!form.unit_no.trim()) next.unit_no = strings.auth.required
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    setServerError(null)
    const area = parseFloat(form.area_m2.replace(',', '.'))
    const rooms = parseInt(form.room_count, 10)
    try {
      await apiFetch('/api/v1/leases', {
        method: 'POST',
        body: JSON.stringify({
          property_id: form.property_id,
          unit_no: form.unit_no.trim(),
          usage_type: form.usage_type,
          type: TYPE_FOR_USAGE[form.usage_type],
          area_m2: Number.isFinite(area) ? area : null,
          room_count: Number.isInteger(rooms) ? rooms : null,
          floor: form.floor.trim() || null,
        }),
      })
      navigate('/lejemaal')
    } catch {
      setServerError(strings.leases.createError)
    } finally {
      setSubmitting(false)
    }
  }

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: 36,
    padding: '0 10px',
    borderRadius: 7,
    border: `1px solid ${errors.property_id ? '#dc2626' : '#e8ecf4'}`,
    fontSize: 13,
    color: '#0f172a',
    background: '#ffffff',
    outline: 'none',
    fontFamily: 'inherit',
  }

  return (
    <AppLayout>
      <div style={{ maxWidth: 520 }}>
        <Card padding="md">
          <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 20, marginTop: 0 }}>{strings.leases.create}</h2>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 6 }}>
                {strings.leases.property}
              </label>
              <select value={form.property_id} onChange={(e) => onPropertyChange(e.target.value)} style={selectStyle}>
                <option value="">{strings.leases.selectProperty}</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.address}</option>
                ))}
              </select>
              {errors.property_id && <p style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>{errors.property_id}</p>}
            </div>

            {bbrStatus !== 'idle' && <BBRBanner status={bbrStatus} strings={strings} />}

            <Input
              label={strings.leases.unit}
              placeholder={strings.leases.unitPlaceholder}
              value={form.unit_no}
              onChange={(e) => setForm({ ...form, unit_no: e.target.value })}
              error={errors.unit_no}
              autoFocus
            />

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <Input
                  label={strings.leases.area}
                  inputMode="decimal"
                  value={form.area_m2}
                  onChange={(e) => setForm({ ...form, area_m2: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Input
                  label={strings.leases.rooms}
                  inputMode="numeric"
                  value={form.room_count}
                  onChange={(e) => setForm({ ...form, room_count: e.target.value })}
                />
              </div>
              <div style={{ flex: 1 }}>
                <Input
                  label={strings.leases.floor}
                  value={form.floor}
                  onChange={(e) => setForm({ ...form, floor: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#64748b', marginBottom: 8 }}>
                {strings.leases.usage}
                {usageFromBbr && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 500, color: '#4f46e5' }}>
                    · {strings.leases.bbrUsageSuggested}
                  </span>
                )}
              </label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {USAGE_TYPES.map((usage) => (
                  <TypeButton
                    key={usage}
                    active={form.usage_type === usage}
                    onClick={() => {
                      setForm({ ...form, usage_type: usage })
                      setUsageFromBbr(false)
                    }}
                  >
                    {usageLabel(usage)}
                  </TypeButton>
                ))}
              </div>
            </div>

            {serverError && <p style={{ fontSize: 12, color: '#dc2626' }}>{serverError}</p>}

            <div style={{ display: 'flex', gap: 8, paddingTop: 4 }}>
              <Button type="submit" isLoading={submitting}>{strings.general.create}</Button>
              <Button type="button" variant="secondary" onClick={() => navigate('/lejemaal')}>{strings.general.cancel}</Button>
            </div>
          </form>
        </Card>
      </div>
    </AppLayout>
  )
}

function BBRBanner({ status, strings }: { status: BBRStatus; strings: ReturnType<typeof t> }) {
  if (status === 'loading') {
    return (
      <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#94a3b8', display: 'inline-block' }} />
        {strings.leases.bbrFetching}
      </div>
    )
  }
  const found = status === 'found'
  return (
    <div
      style={{
        fontSize: 12,
        color: found ? '#15803d' : '#92400e',
        background: found ? '#f0fdf4' : '#fffbeb',
        border: `1px solid ${found ? '#bbf7d0' : '#fde68a'}`,
        borderRadius: 7,
        padding: '8px 10px',
      }}
    >
      {found ? strings.leases.bbrFound : strings.leases.bbrNotFound}
    </div>
  )
}

function TypeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  const [hovered, setHovered] = React.useState(false)
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '6px 14px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        border: `1px solid ${active ? '#4f46e5' : '#e8ecf4'}`,
        background: active ? '#eef2ff' : hovered ? '#f8fafc' : '#ffffff',
        color: active ? '#4f46e5' : '#64748b',
        cursor: 'pointer',
        transition: 'all 0.12s',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  )
}
